import React from 'react'
import { toast } from 'react-toastify'

import 'react-toastify/dist/ReactToastify.css'
import ClassNotification from 'components/common/Notification/ClassNotification'
import {
    MoveToNextQuestionNotification,
    PassQuestionNotification,
} from 'components/common/Notification/NoteNotification'
import ToastNotification from 'components/common/Notification/ToastNotification'
import { gestureIds, gesturesData } from 'components/Tiles/Student/Gestures'
import { CHAT, VIBE } from 'constants/socket'
import { GESTURE } from 'services/webSockets/gestures/constants'
import { playSound } from 'utils/audio'

export const sendNoteUploadNotification = ({ user }) => {
    // play sound
    playSound('/notifications/upload_sound.mp3')

    // display toast notification
    noteUploadNotification({ user })
}

export const sendMoveToNextQuestionNotification = message => {
    // play sound
    playSound('/notifications/move_to_next_question.mp3')

    // display toast notification
    toast.info(<MoveToNextQuestionNotification message={message} />)
}

export const sendPassQuestionNotification = message => {
    // play sound
    playSound('/notifications/pass_question.mp3')

    // display toast notification
    toast.info(<PassQuestionNotification message={message} />)
}

export const sendGenericNotification = (message, options) => {
    // display toast notification
    toast.info(message, options)
}

const notificationOptions = {
    position: 'top-right',
}

const TOAST_MAP = {
    SUCCESS: { color: '#219653', type: 'success' },
    ERROR: { color: '#EB5757', type: 'error' },
    INFO: { color: '#58B7E6', type: 'info' },
    WARN: { color: '#F3C520', type: 'warn' },
}

export const CLASS_NOTIFICATION_TYPES = {
    default: 'default',
    error: 'error',
    success: 'success',
    chat: 'chat',
    call_joined: 'call_joined',
    call_left: 'call_left',
    vibing: 'vibing',
    note_upload: 'note_upload',
    ...gestureIds,
}

/**
 *
 *  This function is used to show the notification toast
 *
 *  @param {string | array} {message} * - The toast message to be displayed. It can either be a single string or an array of strings.
 *  @param {object} {options} ? - The additional options that we can pass to overwrite the available options.
 *  @param {string} options.icon - This is the name of the icon. Save it as toast-<name_of_the_icon> or pass a icon path using the path options.
 *  @param {string} options.iconPath - The icon path that needs to be displayed.
 *  @param {string} options.color - The background color of the icon.
 *  @param {boolean} options.showIcon - To show the icon. Default value is true
 *
 *  ( * ) -> required
 *  ( ? ) -> optional
 *
 *  @example
 *   Default format
 *   notify.success('Action triggered successfully')
 *
 *   Multiple message
 *   notify.success(['You are not registered with us','Follow the steps:','1. Signup first','2. Login once #2 is done'])
 *
 *   With options to hide icon
 *   notify.success('Action triggered successfully',{ showIcon: false })
 *
 *   Error
 *   notify.error('oops, sorry. it's on us and we are working on it.')
 *
 *   Info
 *   notify.info('You need to be registerd fisrt in order to login in.')
 *
 *   Warn
 *   notify.warn('oops... it's not you it's us. We're working on it.')
 *
 */

const getAppropriateNotification = (message, options = {}, type) => {
    const notifyType = type.toUpperCase()
    const props = {
        message,
        options: {
            color: TOAST_MAP[notifyType].color,
            icon: TOAST_MAP[notifyType].type,
            ...options, // overwritable options
        },
    }
    toast[type](<ToastNotification {...props} />, notificationOptions)
}

export const classNotification = props =>
    toast.success(<ClassNotification {...props} />, notificationOptions)

export const notify = {
    success: (message, options = {}) =>
        getAppropriateNotification(message, options, TOAST_MAP.SUCCESS.type),
    error: (message, options = {}) =>
        getAppropriateNotification(message, options, TOAST_MAP.ERROR.type),
    warn: (message, options = {}) =>
        getAppropriateNotification(message, options, TOAST_MAP.WARN.type),
    // TODO: need update for note nofication ui to enable this
    // info: (message, options = {}) =>
    //     getAppropriateNotification(message, options, TOAST_MAP.INFO.type),
}

export function triggerClassNotification(socket_msg_type, data) {
    if (socket_msg_type === GESTURE) {
        gestureNotification(data)
    } else if (socket_msg_type === VIBE) {
        vibeNotification(data)
    } else if (socket_msg_type === CHAT) {
        chatNotification(data)
    }
}

function noteUploadNotification({ user }) {
    classNotification({
        type: CLASS_NOTIFICATION_TYPES.note_upload,
        data: { user },
    })
}

function gestureNotification({ gesture, user }) {
    const _gesture = gesturesData.find(p => p.gestureKey === gesture)
    classNotification({
        type: _gesture.id,
        data: { user },
    })
}

function vibeNotification({ user, user_to }) {
    classNotification({
        type: CLASS_NOTIFICATION_TYPES.vibing,
        data: { user, user_to },
    })
}

function chatNotification({ user, msg }) {
    classNotification({
        type: CLASS_NOTIFICATION_TYPES.chat,
        data: { user, msg },
    })
}
